import {Row} from "@tanstack/react-table";
import {AutoTableInterface} from "@/features/types/AutoTableInterface";

// CLIENT
export interface ClientCRM {
    f_crm_id?: string;
    f_crm_c_typ_desc?: string;
    f_crm_o_name?: string;
    f_crm_r_adr_2?: string;
    f_crm_l_cty?: string;
    f_crm_z_clt?: string;
    f_crm_r_phone_1?: string;
    f_crm_r_clt_ident?: string;
}

// Funkcja pomocnicza do mapowania typu klienta na etykietę NIP/PESEL/IDENT
export const getClientIdentLabel = (clientType?: string): string => {
    if (!clientType || clientType.trim() === "") {
        return "IDENT:";
    }

    const parts = clientType.split("-").map(part => part.trim());
    const firstPart = parts[0];

    if (firstPart === "1") {
        return "NIP:";
    } else if (firstPart === "2") {
        return "PESEL:";
    }

    return "IDENT:";
};

// STATUS
export type StatusCode = string;

// Pobiera dostępne akcje (kody statusów docelowych) dla danego statusu
export const getAvailableActions = (currentStatusCode: string | null): StatusCode[] => {
    const statusCode = currentStatusCode || "";
    return statusToNewStatusMap[statusCode] || [];
}

// Znajduje pełną nazwę statusu na podstawie kodu statusu
export const getNewStatusFromCode = (
    targetStatusCode: StatusCode,
    availableStatuses: string[]
): string | null => {
    const newStatus = availableStatuses.find(status => {
        const statusParts = status.split("-");
        if (statusParts.length < 2) return false;
        const code = statusParts[0].trim();
        return code === targetStatusCode;
    });

    if (!newStatus) {
        console.warn(`Nie znaleziono statusu z kodem "${targetStatusCode}" w dostępnych statusach`);
        return null;
    }

    return newStatus;
}

// Mapowanie: targetStatusCode -> etykieta akcji
// Etykieta opisuje, na jaki status zmieniamy (status docelowy)
// Przykład: "4" -> "Zamówienie" oznacza zmianę na status "4 - Zamówiony"
export const changeStatusActionLabels: Record<StatusCode, string> = {
    "0": "Wydanie",           // Zmiana na "0 - Wydany"
    "1": "Realokacja (f)",   // Zmiana na "1 - Realok. (f)"
    "2": "Realokacja",       // Zmiana na "2 - Realokowany"
    "3": "Oddanie pojazdu",  // Zmiana na "3 - Oddany"
    "4": "Zamówienie",       // Zmiana na "4 - Zamówiony"
    "5": "Rezerwacja",       // Zmiana na "5 - Zarezerw."
    "": "Brak rezerwacji",   // Usunięcie rezerwacji (status -> NULL/pusty)
    "6": "Anulowanie",       // Zmiana na "6 - Anulowany"
}

// Mapowanie: currentStatusCode -> [targetStatusCode1, targetStatusCode2, ...]
// Przykład: status "" (pusty) -> można zmienić na ["5", "4"] (Rezerwacja lub Zamówienie)
// Nazwa akcji jest automatycznie pobierana z changeStatusActionLabels[targetStatusCode]
export const statusToNewStatusMap: Record<StatusCode, StatusCode[]> = {
    "": ["5", "4"],  // NULL/pusty status -> można zmienić na Rezerwację (5) lub Zamówienie (4)
    "5": ["4", ""],      // "5 - Zarezerw." -> można zmienić na Zamówienie (4) lub usunąć rezerwację ("" = NULL)
    "4": ["3"],      // "4 - Zamówiony" -> można zmienić na Oddany (3)
    "3": ["1"],      // "3 - Oddany" -> można zmienić na Realok. (f) (1)
    "2": ["2"],      // "2 - Realokowany" -> można zmienić na Realokowany (2) - ta sama wartość
    "1": ["0"],      // "1 - Realok. (f)" -> można zmienić na Wydany (0)
    "0": ["6"],      // "0 - Wydany" -> można zmienić na Anulowany (6)
    // "6": [],  // "6 - Anulowany" - może nie mieć akcji
}

// ORDERS

export type VehicleType = "Osobowy" | "Ciężarowy";

export interface OrderFormData {
    client: ClientCRM | null;
    ord_vehicle_type: VehicleType | null;
    ord_sales_agr_issue_dat: Date | null;
    ord_sales_agr_registr_dat: Date | null;
    ord_sales_price: string;
}

export interface ReservationFormData {
    f_sales_user_name: string | null;
    reservation_days: number | null;
    reservation_type: "short" | "long" | null; // "short" = krótkoterminowe, "long" = długoterminowe
}

export interface SaveOrderData {
    f_id: string;
    f_loc_lit: string;
    f_ord_vehicle_type: VehicleType;
    f_ord_client_id: string;
    f_ord_user_name: string;
    f_ord_status_desc: string;
    f_ord_sales_agr_issue_dat: string;
    f_ord_sales_agr_registr_dat: string;
    f_ord_sales_price: string;
}

export interface SaveReservationData {
    f_id: string;
    f_loc_lit: string;
    f_ord_num: string; // Numer zamówienia (f_ord_num) - używany do znajdowania rekordu w bazie
    f_ord_d: string;
    f_sales_user_name: string;
    reservation_days: number;
    reservation_type: "short" | "long";
    action_date: string; // Data dzisiejsza + reservation_days (format: YYYY-MM-DD)
    // new_status nie jest potrzebne - backend obliczy "5 - Zarezerw."
}

export interface CancelReservationData {
    f_id: string;
    f_loc_lit: string;
    f_ord_num: string; // Numer zamówienia (f_ord_num) - używany do znajdowania rekordu w bazie
    f_ord_d: string;
}

// Handler tylko dla zamówień - zwraca pełne dane
export type SaveOrderHandler = (args: {
    row: Row<AutoTableInterface>;
    formData: OrderFormData;
    userName: string;
}) => Promise<{
    ord_num: string | null;
    ord_user_name: string | null;
    ord_year: number | null;
    ord_pri_selected: number | null;
}>;

// Handler dla rezerwacji - zwraca tylko success
export type SaveReservationHandler = (args: {
    row: Row<AutoTableInterface>;
    formData: ReservationFormData;
    userName: string;
}) => Promise<{ success: boolean }>;

// Handler dla anulowania rezerwacji - zwraca tylko success
export type CancelReservationHandler = (args: {
    row: Row<AutoTableInterface>;
    userName: string;
}) => Promise<{ success: boolean }>;

// Response dla zamówień - zawiera pełne dane
export interface SaveOrderResponse {
    success: boolean;
    data: {
        ord_num: string | null;
        ord_user_name: string | null;
        ord_year: number | null;
        ord_pri_selected: number | null;
    };
}

// Response dla rezerwacji - tylko success
export interface SaveReservationResponse {
    success: boolean;
}

// Response dla anulowania rezerwacji - tylko success
export interface CancelReservationResponse {
    success: boolean;
}

